import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Hero } from "@/components/home/Hero";
import { FrameworkInteractive } from "@/components/home/FrameworkInteractive";
import { Marquee } from "@/components/common/Marquee";
import { Reveal } from "@/components/common/Reveal";
import { SectionHeading } from "@/components/common/SectionHeading";
import { CTASection } from "@/components/common/CTASection";
import { Icon } from "@/components/common/Icon";
import { SERVICE_GROUPS, ENGAGEMENT_MODELS, WHY_POINTS, IMAGES } from "@/lib/content";

const PROBLEM_ITEMS = [
  "Market entry", "Entity setup", "Hiring", "Payroll", "HR", "Compliance",
  "Taxation", "Finance", "Office setup", "IT", "Procurement", "Employee management",
];

export default function Home() {
  const navigate = useNavigate();

  return (
    <>
      <Hero />

      <Marquee items={["Startups", "SaaS", "AI Companies", "Software", "Scale-ups", "Global Tech"]} />

      {/* PROBLEM */}
      <section className="section-pad relative" data-testid="problem-section">
        <div className="container-px">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <div>
              <SectionHeading
                kicker="The Challenge"
                title="Expanding into India shouldn't be complicated."
                subtitle="Companies entering India often juggle a dozen functions and unfamiliar regulations across multiple vendors. That fragmentation creates cost, delay, and risk."
              />
              <Reveal i={2}>
                <p className="max-w-lg text-zinc-400">
                  Pixel-Hatch is the single, trusted partner that simplifies the entire journey — so
                  you get one accountable team instead of ten disconnected suppliers.
                </p>
                <button onClick={() => navigate("/services")} className="btn-ghost mt-7" data-testid="problem-cta">
                  Explore Our Services <ArrowUpRight className="h-4 w-4" />
                </button>
              </Reveal>
            </div>
            <Reveal i={1}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {PROBLEM_ITEMS.map((item, i) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.03 }}
                    className="rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3 text-sm text-zinc-300"
                  >
                    {item}
                  </motion.div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BRAND MESSAGE */}
      <section className="relative overflow-hidden border-y border-white/10 bg-[#17171A] py-24" data-testid="brand-message">
        <div className="hatch-diag absolute inset-0 opacity-40" />
        <div className="container-px relative text-center">
          <Reveal>
            <p className="kicker mb-6 flex justify-center gap-2">Our Philosophy</p>
          </Reveal>
          <Reveal i={1}>
            <h2 className="mx-auto max-w-4xl font-head text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              <span className="grad-text">Ideas Hatch.</span> Teams Grow. Businesses Scale.
            </h2>
          </Reveal>
          <Reveal i={2}>
            <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg">
              Every global business begins with an idea. Pixel-Hatch helps transform that idea into a
              functioning India operation — and then supports it as your team and business grow.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FRAMEWORK */}
      <section className="section-pad relative" data-testid="framework-section">
        <div className="container-px">
          <SectionHeading
            kicker="From Vision to Victory"
            title="The HATCH Framework™"
            subtitle="A structured, five-stage path for building successful India operations. Each stage maps a customer need to Pixel-Hatch support and a clear business outcome."
            center
          />
          <FrameworkInteractive />
          <Reveal i={2}>
            <div className="mt-10 flex justify-center">
              <button onClick={() => navigate("/framework")} className="btn-ghost" data-testid="framework-cta">
                Explore the Full Framework <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad relative border-t border-white/10 bg-[#131316]" data-testid="services-section">
        <div className="container-px">
          <SectionHeading
            kicker="What We Do"
            title="Everything you need to build your India operations"
            subtitle="Organized into four logical groups so nothing falls through the cracks."
          />
          <div className="grid gap-6 md:grid-cols-2">
            {SERVICE_GROUPS.map((g, i) => (
              <Reveal key={g.id} i={i}>
                <div className="group h-full rounded-2xl border border-white/8 bg-white/[0.02] p-8 transition-all duration-300 hover:border-orange-500/30 hover:bg-white/[0.04]">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-orange-500/20 bg-orange-500/5 text-orange-400">
                    <Icon name={g.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="font-head text-xl font-bold text-white">{g.title}</h3>
                  <ul className="mt-5 space-y-2.5">
                    {g.services.map((s) => (
                      <li key={s.name} className="flex items-start gap-2.5 text-sm text-zinc-400">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                        <span><span className="font-medium text-zinc-200">{s.name}</span></span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal i={2}>
            <div className="mt-10 flex justify-center">
              <button onClick={() => navigate("/services")} className="btn-primary" data-testid="services-cta">
                Explore Services <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ENGAGEMENT MODELS */}
      <section className="section-pad relative" data-testid="engagement-section">
        <div className="container-px">
          <SectionHeading
            kicker="Engagement Models"
            title="One size never fits all"
            subtitle="Choose the model that matches your stage, timeline, and ambition — or blend them as you grow."
          />
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {ENGAGEMENT_MODELS.map((m, i) => (
              <Reveal key={m.id} i={i}>
                <div className="flex h-full flex-col rounded-2xl border border-white/8 bg-gradient-to-b from-white/[0.04] to-transparent p-7 transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/30">
                  <span className="mb-4 w-fit rounded-full border border-orange-500/20 bg-orange-500/5 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-orange-300">
                    {m.tag}
                  </span>
                  <h3 className="font-head text-2xl font-bold text-white">{m.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PIXEL-HATCH */}
      <section className="section-pad relative border-y border-white/10 bg-[#131316]" data-testid="why-section">
        <div className="container-px">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div className="lg:sticky lg:top-28">
              <SectionHeading
                kicker="Why Pixel-Hatch"
                title="Your India expansion. Our local expertise."
                subtitle="We combine on-the-ground knowledge with a structured methodology to reduce your risk and speed up your build."
              />
              <Reveal i={2}>
                <div className="overflow-hidden rounded-2xl border border-white/10">
                  <img src={IMAGES.team2} alt="Pixel-Hatch team" className="h-56 w-full object-cover" />
                </div>
              </Reveal>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {WHY_POINTS.map((p, i) => (
                <Reveal key={p.title} i={i}>
                  <div className="h-full rounded-2xl border border-white/8 bg-white/[0.02] p-6 transition-colors hover:border-orange-500/30">
                    <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg grad-accent text-[#1a0f00]">
                      <Icon name={p.icon} className="h-5 w-5" />
                    </div>
                    <h3 className="font-head text-base font-bold text-white">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-400">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ASSESSMENT CTA */}
      <section className="section-pad" data-testid="assessment-cta-section">
        <div className="container-px">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#17171A]">
            <div className="absolute inset-0">
              <img src={IMAGES.leadership} alt="" className="h-full w-full object-cover opacity-20" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#17171A] via-[#17171A]/90 to-[#17171A]/40" />
            </div>
            <div className="relative grid gap-8 p-10 sm:p-14 lg:grid-cols-2 lg:items-center">
              <div>
                <p className="kicker mb-4">India Readiness Assessment™</p>
                <h2 className="font-head text-3xl font-bold leading-tight text-white sm:text-4xl">
                  How ready is your business for India?
                </h2>
                <p className="mt-5 max-w-md text-zinc-400">
                  Take a short assessment and get a readiness score, a suggested entry model, and
                  recommended next steps — tailored to your goals.
                </p>
                <button onClick={() => navigate("/assessment")} className="btn-primary mt-8" data-testid="assessment-cta">
                  Take the India Readiness Assessment™ <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <div className="grid gap-4 sm:grid-cols-3 lg:justify-items-end">
                {[["Readiness", "Score"], ["Suggested", "Entry Model"], ["Next", "Steps"]].map((t, i) => (
                  <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-center">
                    <div className="font-head text-lg font-bold text-orange-400">{t[0]}</div>
                    <div className="text-xs text-zinc-500">{t[1]}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
